console.log('Cart discount script loaded');

class CartDiscount {
  constructor() {
    // Prevent multiple instances
    if (window.cartDiscountInstance) {
      console.log('CartDiscount instance already exists, skipping...');
      return window.cartDiscountInstance;
    }
    window.cartDiscountInstance = this;
    
    this.discountRules = {
      'DS1': { 'HRD Misc': 25, 'HRD Dampers': 15 },
      'DS2': { 'HRD Misc': 30, 'HRD Dampers': 20 },
      'WD': { 'HRD Misc': 35, 'HRD Dampers': 25 },
      'Master WD': { 'HRD Misc': 40, 'HRD Dampers': 30 }
    };
    this.tierNames = {
      'DS1': 'DS1 Dealer',
      'DS2': 'DS2 Dealer',
      'WD': 'WD Dealer',
      'Master WD': 'Master WD Dealer'
    };
    this.formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    this.init();
  }

  init() {
    console.log('CartDiscount initialized');
    this.updateCartPrices();
    this.displayDealerTier();
    this.bindEvents();
    
    // Additional delayed recalculation to handle any timing issues
    setTimeout(() => {
      console.log('Delayed recalculation after init');
      this.updateCartPrices();
      this.displayDealerTier();
    }, 500);
  }

  bindEvents() {
    // Only bind events once per instance
    if (this.eventsBound) {
      return;
    }
    this.eventsBound = true;

    // Listen for cart updates and re-apply discounts
    document.addEventListener('cart:updated', () => {
      // Use setTimeout to ensure DOM is updated before applying discounts
      setTimeout(() => {
        if (window.cartDiscountInstance) {
          window.cartDiscountInstance.updateCartPrices();
          window.cartDiscountInstance.displayDealerTier();
        }
      }, 100);
    });

    // The cart DRAWER rebuilds without emitting cart:updated: theme.js
    // CartDrawer's ajaxProduct:added handler calls buildCart() (a
    // /cart?view=ajax fetch) and cartMarkup() then replaces [data-products]
    // with the fresh markup, so the event above never fires for that path.
    // Watching the container turns the rebuild mutation itself into the
    // post-render signal - no race with the fetch.
    const drawerItemsRoot = document.querySelector('[data-products]');
    if (drawerItemsRoot && 'MutationObserver' in window) {
      this.drawerWatchActive = true;
      new MutationObserver(() => {
        if (!window.cartDiscountInstance) return;
        setTimeout(() => {
          window.cartDiscountInstance.recalcDrawer();
        }, 50);
      }).observe(drawerItemsRoot, { childList: true });
    }

    // A dealer with existing cart items opening the drawer directly (header
    // icon) never re-triggers the observer: the startup buildCart() mutation
    // ran while the drawer was display:none, so the offsetParent visibility
    // filter skipped every item, and Drawers.open() does not touch
    // [data-products] - it only adds the open classes and emits drawerOpen.
    // Recalculate once the drawer is actually visible.
    document.addEventListener('drawerOpen.CartDrawer', () => {
      if (!window.cartDiscountInstance) return;
      setTimeout(() => {
        window.cartDiscountInstance.recalcDrawer();
      }, 100);
    });

    // Fallback where the container wasn't in the DOM when this script bound:
    // the add event plus a delay still lands after the rebuild in practice.
    // Skipped when the observer above is live (both paths are idempotent,
    // but there is no point running the same recalculation twice).
    document.addEventListener('ajaxProduct:added', () => {
      if (!window.cartDiscountInstance || this.drawerWatchActive) return;
      setTimeout(() => {
        window.cartDiscountInstance.recalcDrawer();
      }, 150);
    });

    // Recalculate when page becomes visible (e.g., navigating back from checkout)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && window.cartDiscountInstance) {
        setTimeout(() => {
          window.cartDiscountInstance.updateCartPrices();
          window.cartDiscountInstance.displayDealerTier();
        }, 100);
      }
    });

    // Recalculate when page gains focus (additional fallback)
    window.addEventListener('focus', () => {
      if (window.cartDiscountInstance) {
        setTimeout(() => {
          window.cartDiscountInstance.updateCartPrices();
          window.cartDiscountInstance.displayDealerTier();
        }, 100);
      }
    });

    // Force recalculation on page load/reload
    window.addEventListener('pageshow', () => {
      console.log('Page show event triggered');
      if (window.cartDiscountInstance) {
        setTimeout(() => {
          window.cartDiscountInstance.updateCartPrices();
          window.cartDiscountInstance.displayDealerTier();
        }, 100);
      }
    });

    // Additional event for page refresh/reload
    window.addEventListener('load', () => {
      console.log('Window load event triggered');
      if (window.cartDiscountInstance) {
        setTimeout(() => {
          window.cartDiscountInstance.updateCartPrices();
          window.cartDiscountInstance.displayDealerTier();
        }, 200);
      }
    });
  }

  displayDealerTier() {
    const customerTag = this.getCustomerTag();
    const dealerTierElement = document.getElementById('dealer-tier');
    
    if (customerTag && dealerTierElement) {
      dealerTierElement.textContent = `Dealer Tier: ${this.tierNames[customerTag]}`;
      dealerTierElement.style.display = 'block';
    }
  }

  // Scoped recalculation for drawer-driven passes. Always priced against the
  // #CartDrawer root so a dealer on /cart with the drawer open cannot have
  // both copies of the line items processed in one pass.
  recalcDrawer() {
    this.updateCartPrices(document.getElementById('CartDrawer') || document);
    this.displayDealerTier();
  }

  // Drawer-scoped passes (drawerOpen, ajaxProduct:added, the [data-products]
  // observer) must never run updateCartPrices() over the whole document: on
  // /cart with the drawer open BOTH copies of the line items are visible, so
  // a global pass would count every line twice and write the doubled subtotal
  // into the first [data-subtotal] in the document.
  updateCartPrices(scopeRoot) {
    console.log('=== Starting cart price update ===');
    
    // Prevent multiple simultaneous calculations
    if (this.isCalculating) {
      console.log('Calculation already in progress, skipping...');
      return;
    }
    this.isCalculating = true;
    
    const customerTag = this.getCustomerTag();
    if (!customerTag) {
      console.log('No customer tag found, skipping discount calculation');
      this.isCalculating = false;
      return;
    }

    const root = scopeRoot || document;
    const cartItems = root.querySelectorAll('.cart__item');
    console.log('Found cart items:', cartItems.length);
    
    // Debug: Log all cart items to identify duplicates
    cartItems.forEach((item, index) => {
      console.log(`Cart item ${index + 1}:`, {
        key: item.dataset.key,
        productType: item.dataset.productType,
        price: item.dataset.price,
        quantity: item.dataset.quantity,
        visible: item.offsetParent !== null,
        display: getComputedStyle(item).display,
        parent: item.parentElement?.className || 'no parent'
      });
    });
    
    if (!cartItems.length) {
      console.log('No cart items found');
      this.isCalculating = false;
      return;
    }
    
    // Filter out hidden or duplicate items
    const validCartItems = Array.from(cartItems).filter((item, index) => {
      const isVisible = item.offsetParent !== null;
      const hasValidData = item.dataset.key && item.dataset.price;
      const isValid = isVisible && hasValidData;
      
      if (!isValid) {
        console.log(`Skipping invalid cart item ${index + 1}:`, {
          key: item.dataset.key,
          visible: isVisible,
          hasValidData: hasValidData
        });
      }
      
      return isValid;
    });
    
    console.log(`Processing ${validCartItems.length} valid items out of ${cartItems.length} total`);
    
    let subtotal = 0;

    validCartItems.forEach((item, index) => {
      const productType = item.dataset.productType;
      // Fix price parsing - ensure we're getting a valid number
      const price = parseFloat(item.dataset.price || '0');
      const priceInCents = Math.round(price * 100);
      const quantity = parseInt(item.dataset.quantity || '1', 10);
      
      console.log(`Processing valid item ${index + 1}/${validCartItems.length}:`, {
        type: productType,
        price: price,
        priceInCents: priceInCents,
        quantity: quantity,
        key: item.dataset.key
      });

      const discountPercent = this.discountRules[customerTag]?.[productType];
      
      if (discountPercent) {
        const discountAmount = Math.round((priceInCents * discountPercent) / 100);
        const finalPriceInCents = priceInCents - discountAmount;

        console.log('Discount:', {
          percent: discountPercent,
          amount: discountAmount,
          finalPrice: finalPriceInCents
        });

        // Add to subtotal
        const itemTotal = finalPriceInCents * quantity;
        subtotal += itemTotal;
        console.log('Item total added to subtotal:', {
          finalPriceInCents,
          quantity,
          itemTotal,
          runningSubtotal: subtotal
        });

        const priceContainer = item.querySelector('.cart__price-group');
        if (priceContainer) {
          const originalPriceEl = priceContainer.querySelector('.cart__original-price');
          const finalPriceEl = priceContainer.querySelector('.cart__final-price');
          
          if (originalPriceEl && finalPriceEl) {
            originalPriceEl.style.display = 'inline-block';
            originalPriceEl.style.textDecoration = 'line-through';
            originalPriceEl.style.color = '#666';
            originalPriceEl.style.marginRight = '10px';
            // LINE totals, matching what Liquid renders for untagged customers
            // (Kevin 2026-08-07). This block overwrites the spans for tagged
            // dealer/wholesale customers, so leaving it on unit prices would
            // have shown THEM a unit price while everyone else saw the line
            // total. priceInCents and finalPriceInCents are both per-unit.
            originalPriceEl.textContent = this.formatMoney(priceInCents * quantity);
            finalPriceEl.textContent = this.formatMoney(itemTotal);
          }
        } else {
          console.warn('Price container not found for item:', item);
        }
      } else {
        // If no discount, add original price to subtotal
        const itemTotal = priceInCents * quantity;
        subtotal += itemTotal;
        console.log('No discount - item total added to subtotal:', {
          priceInCents,
          quantity,
          itemTotal,
          runningSubtotal: subtotal
        });
      }
    });

    console.log('Final subtotal:', subtotal);

    // Update the subtotal display (scoped passes write to their own [data-subtotal])
    const subtotalElement = root.querySelector('[data-subtotal]');
    if (subtotalElement) {
      const formattedSubtotal = this.formatMoney(subtotal);
      subtotalElement.textContent = formattedSubtotal;
      console.log('Updated subtotal display to:', formattedSubtotal);
    } else {
      console.warn('Subtotal element not found');
    }
    
    // Release calculation lock
    this.isCalculating = false;
    console.log('=== Cart price update completed ===');
  }

  formatMoney(cents) {
    return this.formatter.format(cents / 100);
  }

  getCustomerTag() {
    const tagsElement = document.getElementById('customer-tags');
    if (!tagsElement) return null;
    
    const tags = tagsElement.dataset.tags.split(',').map(tag => tag.trim());
    return Object.keys(this.discountRules).find(tag => tags.includes(tag));
  }
}

// Initialize when DOM is ready with multiple fallbacks
function initializeCartDiscount() {
  console.log('Initializing CartDiscount...');
  new CartDiscount();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCartDiscount);
} else {
  initializeCartDiscount();
}

// Additional initialization on window load as fallback
window.addEventListener('load', () => {
  console.log('Window loaded, ensuring CartDiscount is initialized');
  // Check if CartDiscount needs to be re-initialized
  setTimeout(() => {
    const subtotalElement = document.querySelector('[data-subtotal]');
    if (subtotalElement) {
      if (window.cartDiscountInstance) {
        // Use existing instance
        window.cartDiscountInstance.updateCartPrices();
        window.cartDiscountInstance.displayDealerTier();
      } else {
        // Create new instance if none exists
        new CartDiscount();
      }
    }
  }, 100);
});

// Global function to manually trigger recalculation (for debugging)
window.recalculateCartDiscount = function() {
  console.log('Manual recalculation triggered');
  if (window.cartDiscountInstance) {
    window.cartDiscountInstance.updateCartPrices();
    window.cartDiscountInstance.displayDealerTier();
  } else {
    new CartDiscount();
  }
};
